<template>
    <!-- Buy page: "Filter proxies by feature". Every toggle that is on must be offered by a location's server
         for it to stay listed, whether it has stock or not. Always opens with everything off. -->
    <div v-if="shown.length" class="feature-filter">
        <span class="feature-filter__title">{{ t('featureFilter.title') }}</span>
        <button v-for="f in shown" :key="f.key" type="button" class="feature-filter__toggle"
            :class="{ 'is-on': modelValue.includes(f.key) }" role="switch" :aria-checked="modelValue.includes(f.key)"
            @click="toggle(f.key)">
            <span class="feature-filter__switch" aria-hidden="true"></span>
            <img v-if="f.logo" :src="f.logo" class="feature-filter__logo" :class="{ 'feature-filter__logo--disc': f.disc }" alt="" />
            {{ t(f.label) }}
        </button>
        <button v-if="modelValue.length" type="button" class="feature-filter__clear" @click="emit('update:modelValue', [])">{{ t('featureFilter.clear') }}</button>
    </div>
</template>

<script setup>
import { defineEmits, defineProps, ref, computed, onMounted } from 'vue';
import { fetchWrapper } from '@/helpers';
import { t } from '@/i18n';
import openVpnLogo from '@/assets/openVPN.png';
import wireGuardLogo from '@/assets/wireguard.svg';

const props = defineProps({ modelValue: { type: Array, default: () => [] } });
const emit = defineEmits(['update:modelValue']);

// keys match the server Features switches on the admin side (ipv6: the server's IPv6 tick box)
const FEATURES = [
    { key: 'ipv6', label: 'featureFilter.ipv6' },   // the server's IPv6 tick box (admin: Edit server)
    { key: 'openvpn', label: 'featureFilter.openvpn', logo: openVpnLogo },
    { key: 'wireguard', label: 'featureFilter.wireguard', logo: wireGuardLogo, disc: true },   // dark red mark: white disc behind it
    { key: 'os_spoofing', label: 'featureFilter.osSpoofing' },
    { key: 'udp', label: 'featureFilter.udp' },
];
// only the features some ready server offers; none at all: no filter bar
const offered = ref(null);
const shown = computed(() => offered.value ? FEATURES.filter((f) => offered.value[f.key]) : []);
onMounted(async () => {
    try { offered.value = (await fetchWrapper.get(`${process.env.VUE_APP_VUE_API_URL}/package/features-offered`)).features || {}; }
    catch (e) { offered.value = {}; }
});
const toggle = (key) => emit('update:modelValue', props.modelValue.includes(key) ? props.modelValue.filter((k) => k !== key) : [...props.modelValue, key]);
</script>

<style scoped>
/* outlined in the site's accent colour, the same one as the ring and badge on the selected package */
.feature-filter { display: flex; flex-wrap: wrap; align-items: center; gap: 10px 12px; width: fit-content; max-width: 100%; padding: 14px 18px; border: 2px solid var(--ui-pri); border-radius: 14px; background: var(--ui-page); color: var(--ui-ink); }   /* same ground as the city box; the toggles match the city cards */
.feature-filter__title { margin-right: 6px; font-size: 19px; font-weight: 700; }
.feature-filter__toggle { display: inline-flex; align-items: center; gap: 9px; padding: 8px 16px 8px 10px; border: 1px solid var(--ui-line-strong); border-radius: 999px; background: var(--ui-card-2); color: var(--ui-ink); font-size: 15.5px; font-weight: 600; cursor: pointer; transition: border-color 150ms ease, background-color 150ms ease; }
.feature-filter__toggle:hover, .feature-filter__toggle:focus-visible { border-color: var(--ui-pri); outline: none; }
.feature-filter__toggle.is-on { border: 2px solid var(--ui-pri); padding: 7px 15px 7px 9px; background: var(--ui-pri-soft); font-weight: 700; }
.feature-filter__switch { position: relative; flex: none; width: 36px; height: 20px; border-radius: 999px; background: var(--ui-line-strong); transition: background-color 150ms ease; }
.feature-filter__switch::after { content: ""; position: absolute; top: 2px; left: 2px; width: 16px; height: 16px; border-radius: 50%; background: #ffffff; transition: left 150ms ease; }
.feature-filter__toggle.is-on .feature-filter__switch { background: var(--ui-pri); }
.feature-filter__toggle.is-on .feature-filter__switch::after { left: 18px; }
.feature-filter__logo { width: 20px; height: 20px; object-fit: contain; }
.feature-filter__logo--disc { background: #ffffff; border-radius: 50%; padding: 1px; }
.feature-filter__clear { border: 0; background: transparent; color: var(--ui-dim); font-size: 14px; font-weight: 600; text-decoration: underline; cursor: pointer; }
@media (prefers-reduced-motion: reduce) { .feature-filter__toggle, .feature-filter__switch, .feature-filter__switch::after { transition: none; } }
</style>
